import { Fragment, type ComponentType, type ReactNode } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@monksflow/monks-ui';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { formatVersionLabel, type ProjectFileVersion } from '@/entities/project-file';
import { LocalFullscreenProvider, MediaPlayerProvider } from '@/features/comment-on-file';
import { mergeVersionIntoAssetFile, toAssetFile } from '../AssetDetail/assetDetail.types';
import { AssetPreviewOverlay } from '../AssetDetail/components/AssetPreviewOverlay';
import { useTranslation } from 'react-i18next';
import { useSyncPlay } from './useSyncPlay';
import { PreviewZoomProvider } from '../AssetDetail/components/AssetPreviewOverlay/state/preview-zoom.store';
import { PdfViewerProvider } from '../AssetDetail/components/PdfViewer/pdfViewer.store';
import { AssetDetailFullScreen } from '../AssetDetail/components/Fullscreen';

interface Props {
  side: 'left' | 'right';
  file: ProjectFileResponseDto;
  version: ProjectFileVersion;
  allVersions: ProjectFileVersion[];
  /** Version id rendered in the other pane — disabled here so the user can't
   * pick the same version on both sides. */
  otherVersionId: string;
  /** True when this pane is showing the newest version. Approve is file-level,
   * so it's only meaningful when the latest is in view. */
  isLatest: boolean;
  /** Required by AssetDetailFullScreen's top bar contract; the compare top
   * bar never navigates (no sibling files) so these are passed through but
   * never read at the route level. */
  projectId: string;
  folderId: string;
  onSelectVersion: (versionId: string) => void;
  /** Approve control or read-only chain progress, rendered only on the latest version. */
  approvalControl?: ReactNode;
}

/**
 * One side of the compare-versions view. Each pane gets its own
 * <PreviewZoomProvider> / <PdfViewerProvider> / <MediaPlayerProvider> so
 * zoom / page / playback state never crosses pane boundaries. Annotation
 * surfaces (ShapeOverlay, PdfPageOverlay, VideoControls markers) are
 * suppressed via `annotationsEnabled={false}` on the preview — the global
 * comments store would otherwise leak composer state (selected tool, draft
 * shapes, soloed comments) from the single-asset view into compare.
 */
export function AssetComparePane({
  side,
  file,
  version,
  allVersions,
  otherVersionId,
  isLatest,
  projectId,
  folderId,
  onSelectVersion,
  approvalControl,
}: Props) {
  const previewFile = mergeVersionIntoAssetFile(toAssetFile(file), version);

  const isImage = previewFile.assetType === 'image';
  const isVideo = previewFile.assetType === 'video';
  const isPdf = previewFile.assetType === 'document' && previewFile.fileType === 'application/pdf';

  const { t } = useTranslation('projects');
  const syncPlay = useSyncPlay();

  const sideLabel =
    side === 'left'
      ? t('projectDetail.assetComparePane.leftVersion')
      : t('projectDetail.assetComparePane.rightVersion');

  const handleValueChange = (nextId: string) => {
    if (nextId && nextId !== version.id) onSelectVersion(nextId);
  };

  // Mount only the providers whose stores the active overlay actually reads,
  // so two compare panes never collide on a store they don't need.
  const PreviewZoom = isImage ? PreviewZoomProvider : PassthroughProvider;
  const PdfViewer = isPdf ? PdfViewerProvider : PassthroughProvider;
  const MediaPlayer = isVideo ? MediaPlayerProvider : PassthroughProvider;

  return (
    <PreviewZoom>
      <PdfViewer>
        <MediaPlayer>
          <LocalFullscreenProvider>
            <div
              className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card"
              aria-label={sideLabel}
            >
              <div className="flex items-center justify-between gap-2 border-b border-border p-4">
                <Select value={version.id} onValueChange={handleValueChange}>
                  <SelectTrigger
                    className="h-9 w-32"
                    aria-label={t('projectDetail.assetComparePane.versionSelector', {
                      side: sideLabel,
                    })}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allVersions.map((v) => (
                      <SelectItem key={v.id} value={v.id} disabled={v.id === otherVersionId}>
                        {t('projectDetail.assetComparePane.version', {
                          versionLabel: formatVersionLabel(v.versionNumber),
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isLatest && approvalControl}
              </div>
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <AssetDetailFullScreen
                  file={previewFile}
                  siblingFiles={[]}
                  projectId={projectId}
                  folderId={folderId}
                >
                  <AssetPreviewOverlay
                    file={previewFile}
                    annotationsEnabled={false}
                    syncPlay={isVideo ? syncPlay : undefined}
                  />
                </AssetDetailFullScreen>
              </div>
            </div>
          </LocalFullscreenProvider>
        </MediaPlayer>
      </PdfViewer>
    </PreviewZoom>
  );
}

const PassthroughProvider: ComponentType<{ children: ReactNode }> = ({ children }) => (
  <Fragment>{children}</Fragment>
);
